import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';

export default function AdminStockConsumption() {
  const showToast = useToast();
  const [strains, setStrains] = useState([]);
  const [strainId, setStrainId] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get('/admin/strains/all').then(s => setStrains(s.filter(st => st.is_in_stock))).catch(() => {});
  }, []);

  const selected = strains.find(s => s.id === strainId);
  const totalCost = selected ? parseFloat(selected.total_cost) || 0 : 0;
  const startingStock = selected ? parseFloat(selected.starting_stock_grams) || 0 : 0;
  const costPerGram = startingStock > 0 ? totalCost / startingStock : 0;
  const parsedAmount = parseFloat(amount) || 0;
  const costValue = Math.round(costPerGram * parsedAmount * 100) / 100;
  const stockAvailable = selected ? parseFloat(selected.stock_grams) || 0 : 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!strainId || parsedAmount <= 0) {
      setError('Select a strain and enter a valid amount');
      return;
    }
    if (parsedAmount > stockAvailable) {
      setError(`Not enough stock. Available: ${stockAvailable}g`);
      return;
    }
    setConfirm(true);
  };

  const handleConfirm = async () => {
    setConfirm(false);
    setSaving(true);
    setError('');
    try {
      await api.post('/admin/stock-consumption', { strain_id: strainId, amount: parsedAmount });
      showToast('success', 'Stock deducted for admin use.');
      setStrainId('');
      setAmount('');
      api.get('/admin/strains/all').then(s => setStrains(s.filter(st => st.is_in_stock))).catch(() => {});
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 4 }}>Admin Stock Consumption</h3>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        Take product from stock for personal use — no sale, no points
      </p>

      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <select value={strainId} onChange={e => setStrainId(e.target.value)} required>
            <option value="">Select strain...</option>
            {strains.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({parseFloat(s.stock_grams)}g avail)</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Amount (grams)"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            min="0.1"
            max={stockAvailable || undefined}
            step="0.1"
            required
          />
        </div>

        {/* Cost preview */}
        {selected && parsedAmount > 0 && (
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 12, fontSize: 13,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Cost per gram</span>
              <span style={{ fontWeight: 600 }}>{costPerGram.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Total cost value</span>
              <span style={{ fontWeight: 700, color: 'var(--warning)' }}>{costValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
            </div>
          </div>
        )}

        <button type="submit" className="btn-primary btn-small" disabled={saving || !strainId || parsedAmount <= 0}>
          {saving ? 'Deducting...' : 'Deduct From Stock'}
        </button>
      </form>

      {/* Confirmation modal */}
      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>Confirm Stock Consumption</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
              You are about to deduct <strong>{parsedAmount}g</strong> of <strong>{selected?.name}</strong> for admin use.
              This will reduce stock and remove the cost value (<strong>{costValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>) from sales stats.
              This will <strong>NOT</strong> create a sale or award points/spins. Proceed?
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary btn-small" onClick={handleConfirm}>Yes, Deduct</button>
              <button className="btn-secondary btn-small" onClick={() => setConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
